import React, { useRef, useState } from 'react';
import TurnstileField, { widgetErrorMessage } from './TurnstileField';

export default function QuoteModal({ onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    size: '1-10',
    industry: 'Corporate',
    details: ''
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const turnstileRef = useRef(null);

  function handleChange(event) {
    setFormData((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  }

  async function submit(event) {
    event.preventDefault();

    if (!turnstileToken) {
      setStatus({ ok: false, msg: 'Please verify that you are human.' });
      return;
    }

    setLoading(true);

    const compiledMessage = `
QUOTE REQUEST
-------------
Name: ${formData.name}
Company: ${formData.companyName}
Size: ${formData.size}
Industry: ${formData.industry}
-------------
Project Details:
${formData.details}
    `;

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: `Quote Request from ${formData.companyName}`,
          message: compiledMessage,
          turnstileToken
        })
      });

      const data = await response.json();
      if (response.ok) {
        setStatus({ ok: true, msg: 'Quote request sent successfully.' });
        setTurnstileToken('');
        setTurnstileError('');
        turnstileRef.current?.reset?.();
        setTimeout(onClose, 1800);
      } else {
        setStatus({ ok: false, msg: data.error || 'Failed to send request.' });
        turnstileRef.current?.reset?.();
        setTurnstileToken('');
      }
    } catch (error) {
      setStatus({ ok: false, msg: 'Network error. Please try again.' });
      turnstileRef.current?.reset?.();
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal card">
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3>Request a Quote</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 0, fontSize: 16, cursor: 'pointer', color: '#24364a' }}
            aria-label="Close quote form"
          >
            Close
          </button>
        </header>

        <form onSubmit={submit} className="modal-form">
          <div className="modal-intro">
            <p>Share a few project details and we will respond with the right next step.</p>
          </div>

          <div className="modal-grid-two">
            <input required name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} />
            <input required name="companyName" placeholder="Company Name" value={formData.companyName} onChange={handleChange} />
          </div>

          <div className="modal-grid-two">
            <input required type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} />
            <input required name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} />
          </div>

          <div className="modal-grid-two">
            <select name="size" value={formData.size} onChange={handleChange}>
              <option value="1-10">1-10 Employees</option>
              <option value="11-50">11-50 Employees</option>
              <option value="50+">50+ Employees</option>
            </select>
            <select name="industry" value={formData.industry} onChange={handleChange}>
              <option value="Corporate">Corporate Office</option>
              <option value="Healthcare">Healthcare / Clinic</option>
              <option value="Education">Education / School</option>
              <option value="Retail">Retail / Shop</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <textarea required name="details" placeholder="Project details" rows="4" value={formData.details} onChange={handleChange} />

          <TurnstileField
            ref={turnstileRef}
            onVerify={(token) => {
              setTurnstileToken(token);
              setTurnstileError('');
            }}
            onExpire={() => {
              setTurnstileToken('');
              setTurnstileError('Verification expired. Please complete it again.');
            }}
            onError={() => {
              setTurnstileToken('');
              setTurnstileError(widgetErrorMessage);
            }}
            errorMessage={turnstileError}
          />

          {status && <div className={`modal-status ${status.ok ? 'is-success' : 'is-error'}`}>{status.msg}</div>}

          <div className="modal-actions modal-actions-single">
            <button className="btn" type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(7, 17, 29, 0.72);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 1000;
        }

        .modal.card {
          width: 100%;
          max-width: 680px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 24px;
          border-radius: 28px;
          background: #ffffff;
          box-shadow: 0 24px 60px rgba(7, 17, 29, 0.28);
        }

        .modal-grid-two {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        @media (max-width: 640px) {
          .modal-grid-two {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
